import { useEffect, useMemo, useState } from "react";
import { Save, RefreshCw, Send, Check, AlertCircle, Loader2, Webhook as WebhookIcon, Copy, ChevronRight } from "lucide-react";
import api, { formatDetail, shortDate } from "@/lib/api";

export default function WebhooksPage() {
    const [info, setInfo] = useState(null);
    const [url, setUrl] = useState("");
    const [savingUrl, setSavingUrl] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [copied, setCopied] = useState("");

    const load = async () => {
        const [w, d] = await Promise.all([
            api.get("/partner/webhook"),
            api.get("/partner/webhook/deliveries"),
        ]);
        setInfo(w.data);
        setUrl(w.data.webhook_url || "");
        setDeliveries(d.data.items || []);
    };
    useEffect(() => { load(); }, []);

    const saveUrl = async () => {
        setSavingUrl(true); setTestResult(null);
        try {
            const v = url.trim() || null;
            await api.patch("/partner/webhook", { webhook_url: v });
            await load();
        } catch (e) {
            alert(formatDetail(e?.response?.data?.detail) || "Save failed");
        } finally { setSavingUrl(false); }
    };
    const rotateSecret = async () => {
        if (!window.confirm("Rotate the webhook secret? Old signatures will stop verifying immediately — make sure to update your endpoint with the new secret.")) return;
        setRotating(true);
        try { await api.post("/partner/webhook/rotate-secret"); await load(); }
        finally { setRotating(false); }
    };
    const sendTest = async () => {
        setTesting(true); setTestResult(null);
        try {
            const r = await api.post("/partner/webhook/test");
            setTestResult(r.data);
            await load();
        } catch (e) {
            setTestResult({ ok: false, error: formatDetail(e?.response?.data?.detail) || "Test failed" });
        } finally { setTesting(false); }
    };
    const copy = (k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };

    const verifySnippet = useMemo(() => `# Node.js / Express
app.post('/your/webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const raw = req.body;                                   // Buffer of the raw POST body
  const sig = req.header('X-PG-Signature') || '';         // 'sha256=<hex>'
  const expected = 'sha256=' + crypto
    .createHmac('sha256', process.env.PG_WEBHOOK_SECRET)
    .update(raw).digest('hex');
  if (!crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected)))
    return res.status(401).end();
  const evt = JSON.parse(raw.toString('utf8'));
  // evt.event = 'txn.success' | 'txn.failed'
  // evt.txn_id, evt.amount, evt.utr, evt.customer_username, evt.external_customer_id
  return res.status(200).json({ ok: true });
});

# Python / FastAPI
@app.post('/your/webhook')
async def webhook(req: Request):
    raw = await req.body()
    sig = req.headers.get('X-PG-Signature', '')
    expected = 'sha256=' + hmac.new(SECRET.encode(), raw, 'sha256').hexdigest()
    if not hmac.compare_digest(sig, expected): raise HTTPException(401)
    evt = json.loads(raw)
    return {'ok': True}`, []);

    if (!info) return <div className="text-slate-500">Loading…</div>;

    const headers = [
        ["X-PG-Event", "txn.success or txn.failed"],
        ["X-PG-Signature", "sha256=<hex of HMAC-SHA256(webhook_secret, raw_body)>"],
        ["X-PG-Delivery", "Unique UUID per delivery — use for idempotency"],
        ["X-PG-Partner", "Your partner_id"],
    ];

    return (
        <div className="space-y-5 max-w-4xl">
            <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                    <WebhookIcon className="w-6 h-6 text-blue-600" /> Webhooks
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    We POST a signed JSON payload to your URL the moment a transaction is approved or rejected.
                    Verify the <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">X-PG-Signature</code> header against your secret to authenticate the request.
                </p>
            </div>

            <div className="card p-5 space-y-4">
                <div>
                    <label className="label">Webhook URL</label>
                    <div className="flex gap-2">
                        <input
                            data-testid="webhook-url-input"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://your-app.example.com/paygateway/webhook"
                            className="input font-mono text-sm flex-1"
                        />
                        <button data-testid="webhook-url-save" onClick={saveUrl} disabled={savingUrl}
                            className="btn-primary px-4 py-2 rounded-md flex items-center gap-2 text-sm disabled:opacity-50">
                            {savingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Must be HTTPS in production. Leave blank to disable webhooks.</p>
                </div>

                <div>
                    <label className="label">Webhook secret</label>
                    <div className="flex gap-2">
                        <input data-testid="webhook-secret-display" readOnly value={info.webhook_secret || ""}
                            className="input font-mono text-sm flex-1" />
                        <button data-testid="webhook-copy-secret" onClick={() => copy("s", info.webhook_secret)} className="btn-secondary px-3 py-2 rounded-md">
                            {copied === "s" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button data-testid="webhook-rotate-secret" onClick={rotateSecret} disabled={rotating}
                            className="btn-secondary px-3 py-2 rounded-md flex items-center gap-1.5 text-sm">
                            {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Rotate
                        </button>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-1">Store this in your secret manager. Rotating immediately invalidates the old secret.</p>
                </div>

                <div className="pt-2 border-t border-slate-100">
                    <button data-testid="webhook-test-btn" onClick={sendTest} disabled={!info.webhook_url || testing}
                        className="btn-emerald px-4 py-2 rounded-md flex items-center gap-2 text-sm disabled:opacity-50">
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test ping
                    </button>
                    {testResult && (
                        <div data-testid="webhook-test-result" className={`mt-3 rounded-md p-3 text-sm ${testResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
                            {testResult.ok
                                ? <div className="flex items-center gap-2"><Check className="w-4 h-4" /> HTTP {testResult.status_code} — your endpoint is reachable. {testResult.response_excerpt && <code className="ml-1 font-mono text-[11px]">{testResult.response_excerpt}</code>}</div>
                                : <div className="flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5" /> {testResult.error || "Failed"}</div>}
                        </div>
                    )}
                </div>
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60">
                    <span className="font-semibold text-sm">Headers we send</span>
                </div>
                <div className="p-5">
                    <table className="w-full text-sm">
                        <tbody>
                            {headers.map(([h, desc]) => (
                                <tr key={h} className="border-b border-slate-100 last:border-0">
                                    <td className="py-2 pr-3 font-mono text-[12px] whitespace-nowrap">{h}</td>
                                    <td className="py-2 text-slate-600 text-xs">{desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center justify-between">
                    <span className="font-semibold text-sm">Verify the signature (sample)</span>
                </div>
                <pre className="bg-slate-900 text-slate-100 p-4 text-[12px] leading-relaxed font-mono overflow-x-auto whitespace-pre">{verifySnippet}</pre>
            </div>

            <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <span className="font-semibold text-sm">Recent deliveries</span>
                    <span className="ml-auto text-[11px] text-slate-400">last 100</span>
                </div>
                {deliveries.length === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-slate-400" data-testid="webhook-deliveries-empty">No deliveries yet. Set your webhook URL and approve a transaction.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-2 text-left">When</th>
                                <th className="px-4 py-2 text-left">Event</th>
                                <th className="px-4 py-2 text-left">Txn</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-left">Attempts</th>
                                <th className="px-4 py-2 text-left">Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deliveries.map((d) => (
                                <tr key={d.delivery_id} data-testid={`webhook-delivery-${d.delivery_id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                                    <td className="px-4 py-2 text-xs text-slate-500">{shortDate(d.delivered_at)}</td>
                                    <td className="px-4 py-2 font-mono text-xs">{d.event}</td>
                                    <td className="px-4 py-2 font-mono text-[11px]">{d.txn_id || "—"}</td>
                                    <td className="px-4 py-2">
                                        {d.delivered
                                            ? <span className="pill bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">{d.status_code || "OK"}</span>
                                            : <span className="pill bg-rose-50 text-rose-700 ring-1 ring-rose-200" title={d.error}>{d.status_code || "fail"}</span>}
                                    </td>
                                    <td className="px-4 py-2 text-xs">{d.attempts}</td>
                                    <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-[160px]">{d.source}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
